import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { AppContext, useAppContext } from 'services/context';
import { extractTranslationsBy2PropertiesAndLanguage } from 'services/utils';

export function useScreenSize() {
	const isClient = typeof window === 'object';

	function getSize() {
		return {
			width: isClient ? window.innerWidth : undefined,
			height: isClient ? window.innerHeight : undefined,
		};
	}

	const [windowSize, setWindowSize] = useState(getSize);

	useEffect(() => {
		if (!isClient) {
			return false;
		}

		function handleResize() {
			setWindowSize(getSize());
		}

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
		// eslint-disable-next-line
	}, []); // Empty array ensures that effect is only run on mount and unmount

	return windowSize;
}

export function useTraceUpdate(props) {
	const prev = useRef(props);
	useEffect(() => {
		const changedProps = Object.entries(props).reduce((ps, [k, v]) => {
			if (prev.current[k] !== v) {
				ps[k] = [prev.current[k], v];
			}
			return ps;
		}, {});
		if (Object.keys(changedProps).length > 0) {
			console.log('Changed props:', changedProps);
		}
		prev.current = props;
	});
}

export const useElementOnScreen = options => {
	const containerRef = React.useRef(null);
	const [isVisible, setIsVisible] = React.useState(false);

	const callbackFunction = entries => {
		const [entry] = entries;
		setIsVisible(entry.isIntersecting);
	};
	React.useEffect(() => {
		const observer = new IntersectionObserver(callbackFunction, options);
		if (containerRef.current) observer.observe(containerRef.current);

		return () => {
			if (containerRef.current) observer.unobserve(containerRef.current);
		};
	}, [containerRef, options]);

	return [containerRef, isVisible];
	// Usage
	// Returns the tuple [ref, state] -> [containerRef, isVisible]
	// must set ref to element that want to observe
	// <div calssName="is_on_screen" ref={containerRef} >
};

export const useLocalStorage = (key, initialValue) => {
	const [storedValue, setStoredValue] = useState(() => {
		try {
			const item = window.localStorage.getItem(key);
			return item ? JSON.parse(item) : initialValue;
		} catch (err) {
			console.error(err);
			return initialValue;
		}
	});

	const setValue = value => {
		try {
			const valueToStore = value instanceof Function ? value(storedValue) : value;
			setStoredValue(valueToStore);
			window.localStorage.setItem(key, JSON.stringify(valueToStore));
		} catch (err) {
			console.error(err);
		}
	};

	return [storedValue, setValue];
};

export const useSessionStorage = (key, initialValue) => {
	const [storedValue, setStoredValue] = useState(() => {
		try {
			const item = window.sessionStorage.getItem(key);
			return item ? JSON.parse(item) : initialValue;
		} catch (err) {
			console.error(err);
			return initialValue;
		}
	});

	const setValue = value => {
		try {
			const valueToStore = value instanceof Function ? value(storedValue) : value;
			setStoredValue(valueToStore);
			window.sessionStorage.setItem(key, JSON.stringify(valueToStore));
		} catch (err) {
			console.error(err);
		}
	};

	return [storedValue, setValue];
};

export function useToggle(initialValue = false) {
	return React.useReducer(state => !state, initialValue);
	// Usage
	// Returns the tuple [state, dispatch]
	// const [isOn, toggleIsOn] = useToggle();
	// <button onClick={toggleIsOn}> Turn  {isOn ? 'Off' : 'On'} </button>
}

export const useAuth = () => {
	const { user } = useContext(AppContext);
	return user;
	//if you using useSessionStorage insted of useContext the app wont redirect the user to main page after auto login - he will stay at the same page
	// const [cacheUser, _] = useSessionStorage('user', '');
	// return cacheUser;
};

export const useElementSize = elementRef => {
	const [elementSize, setElementSize] = useState({
		width: 0,
		height: 0,
	});
	const listener = useCallback(
		() =>
			elementRef &&
			elementRef.current &&
			setElementSize({
				width: elementRef.current.offsetWidth,
				height: elementRef.current.offsetHeight,
			}),
		[elementRef]
	);
	useEffect(() => {
		listener();
		window.addEventListener('resize', listener);
		return () => window.removeEventListener('resize', listener);
	}, []);
	return elementSize;
};

export function useTranslation() {
	const { lang, generalTranslations } = useAppContext();

	const getTranslation = useCallback(
		(firstKey, secondKey) => {
			return extractTranslationsBy2PropertiesAndLanguage(generalTranslations, firstKey, secondKey, lang);
		},
		[lang, generalTranslations]
	);

	return getTranslation;
}
